import * as stylex from '@stylexjs/stylex';
import { createContext, type ForwardedRef, forwardRef, useRef } from 'react';
import {
  Column as ColumnCore,
  type ColumnProps as ColumnCoreProps,
  ColumnResizer as ColumnResizerCore,
  composeRenderProps,
  type ContextValue,
  DEFAULT_SLOT,
  Group as GroupCore,
  Provider,
  useContextProps,
} from 'react-aria-components';

import { TextContext } from '@/libs/serenity-ui';
import { $ } from '@/libs/serenity-ui/tokens.stylex';

interface ColumnProps extends Omit<ColumnCoreProps, 'style' | 'className'> {
  /** Whether the column is the first visible column. @default false */
  isFirst?: boolean;
  /** Whether the table is displayed in full screen mode. @default false */
  isFullScreen?: boolean;
  /** Whether the column is the last visible column. @default false */
  isLast?: boolean;
  /** The maximum width of the column. Used to set the width of the column when double clicked. */
  maxContentWidth?: number;
  /** Whether the column is padded. @default false */
  isPadded?: boolean;
  /** Whether the column is resizable. @default false */
  allowsResizing?: boolean;
  style?: stylex.StyleXStyles;
}

const ColumnContext = createContext<ContextValue<ColumnProps, HTMLDivElement>>({});

const Column = forwardRef((props: ColumnProps, ref: ForwardedRef<HTMLDivElement>) => {
  const [ mergedProps, mergedRef ] = useContextProps(props, ref, ColumnContext);

  const { style, isFirst = false, isFullScreen = false, isLast = false, isPadded = false, maxContentWidth, ...coreProps } = mergedProps;

  const isResizingRef = useRef(false);
  const columnResizerRef = useRef<HTMLDivElement>(null);

  const handleDoubleClick = () => {
    if (!maxContentWidth || !columnResizerRef.current || isResizingRef.current) {
      return;
    }

    const resizerRect = columnResizerRef.current.getBoundingClientRect();
    const columnHeader = columnResizerRef.current.closest('[role="columnheader"]');
    const currentWidth = columnHeader ? resizerRect.left - columnHeader.getBoundingClientRect().left : 0;

    // Only resize if there's a meaningful difference
    if (Math.abs(maxContentWidth - currentWidth) <= 1) {
      return;
    }

    // Prevent concurrent resizes
    isResizingRef.current = true;

    const startX = resizerRect.left;
    const targetX = startX + (maxContentWidth - currentWidth);
    const pointerId = 1;
    const sharedProps = {
      bubbles: true,
      cancelable: true,
      pointerId,
      clientY: resizerRect.top + resizerRect.height / 2,
      button: 0,
      isPrimary: true,
    };

    columnResizerRef.current.dispatchEvent(
      new PointerEvent('pointerdown', { ...sharedProps, clientX: startX, buttons: 1, pressure: 1 }),
    );

    requestAnimationFrame(() => {
      document.dispatchEvent(
        new PointerEvent('pointermove', { ...sharedProps, clientX: targetX, buttons: 1, pressure: 1 }),
      );

      requestAnimationFrame(() => {
        document.dispatchEvent(
          new PointerEvent('pointerup', { ...sharedProps, clientX: targetX, buttons: 0, pressure: 0 }),
        );
        isResizingRef.current = false;
      });
    });
  };

  return (
    <ColumnCore
      ref={mergedRef}
      {...stylex.props(stylex.defaultMarker(), styles.base(mergedProps.minWidth ?? 0), !isFullScreen && isLast && styles.borderless, mergedProps.allowsSorting && styles.sortable, style)}
      {...coreProps}>
      {composeRenderProps(mergedProps.children, (children, renderProps) => (
        <Provider
          values={[
            [
              TextContext, {
                slots: {
                  [DEFAULT_SLOT]: {
                    style: textStyles.base,
                    size: 14,
                    color: renderProps.sortDirection ? 'primary' : 'secondary',
                  },
                  [COLUMN_SLOTS.UNIT_TEXT]: {
                    style: textStyles.base,
                    size: 14,
                    color: 'quaternary',
                  },
                },
              },
            ],
          ]}>
          <GroupCore
            role="presentation"
            tabIndex={-1}
            {...stylex.props(
              columnNameStyles.base,
              isPadded && columnNameStyles.padded,
              !isFullScreen && isPadded && columnNameStyles.embeddedPadded,
              !isFullScreen && isFirst && columnNameStyles.firstEmbedded,
              !isFullScreen && isLast && columnNameStyles.lastEmbedded,
            )}>
            {children || renderProps.defaultChildren}
          </GroupCore>
          {mergedProps.allowsResizing && (
            <ColumnResizerCore
              ref={columnResizerRef}
              onDoubleClick={handleDoubleClick}
              {...stylex.props(columnResizerStyles.base)}
            />
          )}
        </Provider>
      ))}
    </ColumnCore>
  );
});

const styles = stylex.create({
  base: (minWidth: number | string | null) => ({
    backgroundColor: {
      default: $.surfaceBGPrimary,
      ':hover': $.surfaceBGSecondary, // Column hover works only if the column is sortable, but we want to show the hover state regardless of sortability
      ':is([data-resizing])': $.surfaceBGSecondary,
    },
    borderRightColor: $.border06,
    borderRightStyle: 'solid',
    borderRightWidth: 1,
    boxSizing: 'border-box',
    color: $.textTertiary,
    cursor: 'default',
    height: 36,
    minWidth,
    overflow: 'hidden',
    padding: 0,
    position: 'relative',
  }),
  borderless: {
    borderRightWidth: 0,
  },
  sortable: {
    cursor: 'pointer',
  },
});

const columnNameStyles = stylex.create({
  base: {
    alignItems: 'center',
    backgroundColor: {
      default: $.surfaceBGSecondary,
      [stylex.when.ancestor(':is([data-resizing])')]: $.opacity04,
      [stylex.when.ancestor(':hover')]: $.opacity04,
    },
    display: 'flex',
    gap: 4,
    height: '100%',
    overflow: 'hidden',
    padding: 0,
    paddingInline: 12,
    textOverflow: 'ellipsis',
    width: '100%',
  },
  padded: { paddingLeft: 20 },
  embeddedPadded: { paddingLeft: 8 },
  firstEmbedded: {
    borderBottomLeftRadius: 6,
    borderTopLeftRadius: 6,
  },
  lastEmbedded: {
    borderBottomRightRadius: 6,
    borderTopRightRadius: 6,
  },
});

const columnResizerStyles = stylex.create({
  base: {
    backgroundImage: {
      default: 'linear-gradient(to right, transparent 6px, transparent 6px, transparent 8px, transparent 8px)',
      ':is([data-focus-visible], [data-resizing], [data-hovered])': `linear-gradient(to right, transparent 6px, ${$.borderInteractive} 6px, ${$.borderInteractive} 8px, transparent 8px)`,
    },
    cursor: {
      default: null,
      ':is([data-resizable-direction="both"])': 'ew-resize',
      ':is([data-resizable-direction="left"])': 'e-resize',
      ':is([data-resizable-direction="right"])': 'w-resize',
    },
    height: '100%',
    marginInline: 4,
    position: 'absolute',
    right: -11,
    top: 0,
    width: 14,
    zIndex: 2,
  },
});

const textStyles = stylex.create({
  base: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

const COLUMN_SLOTS = {
  UNIT_TEXT: 'unit-text',
};

export { Column, COLUMN_SLOTS, ColumnContext, type ColumnProps };
